import { Input } from 'antd';
import Form, { Rule } from 'antd/es/form';
import React, { useCallback, useEffect, useRef } from 'react';
import { useMediaQuery } from 'react-responsive';
import { useTheme } from 'styled-components';

import { Button, Text } from '@desygner/ui-common-components';

import { CONSTANTS } from '../../AuthModal.data';
import {
  StyledAuthFormContainer,
  StyledModalSubtitle,
  StyledOrDivider,
} from '../../AuthModal.styles';
import { AuthViewPropsT, SignupEmailViewLabelsT } from '../../AuthModal.types';
import { focusInput, handleEnter } from '../../AuthModal.utils';
import { Disclaimer } from '../../components/Disclaimer/Disclaimer';
import { SocialProvidersView } from '../../components/SocialProviders/SocialProviders';
import { StepHeader } from '../../components/StepHeader/StepHeader';
import { useAuthContext } from '../../context/AuthContext';

export const SignupEmailView = ({ ...props }: AuthViewPropsT) => {
  const { ...labels } = props.labels as SignupEmailViewLabelsT;
  const theme = useTheme();
  const form = Form.useFormInstance();
  const emailInputRef = useRef<any>(null);
  const isScreenLarge = useMediaQuery({ minWidth: theme.Screen.Size.Large });

  const rules: Rule[] = [
    {
      required: true,
      message: labels.errors.client.required,
    },
    {
      type: 'email',
      message: labels.errors.client.email,
    },
  ];

  useEffect(() => {
    focusInput(emailInputRef);
  }, []);

  const { isLoading, handleLookup, isAuthCloseDisabled } = useAuthContext();

  const onLookup = useCallback(async () => {
    if (isLoading) return;

    return handleLookup?.(form, props.onChangeView, labels.errors.e500);
  }, [form, handleLookup, isLoading, labels.errors.e500, props.onChangeView]);

  return (
    <>
      <StepHeader
        title={labels.title}
        onBack={props.onClose}
        isBackButtonHidden={isScreenLarge || isAuthCloseDisabled}
      />
      <StyledModalSubtitle>
        <Text size="Body">{labels.subtitle}</Text>
      </StyledModalSubtitle>

      <SocialProvidersView
        isCompact
        anotherWayLabel={labels.cta.anotherWay}
        continueWithLabel={labels.cta.continueWith}
      />

      <StyledOrDivider>
        <Text size="Body">{labels.or}</Text>
      </StyledOrDivider>
      <StyledAuthFormContainer>
        <Form.Item name={CONSTANTS.form.emailFieldName} rules={[...rules]}>
          <Input
            placeholder={labels.form.placeholder}
            ref={emailInputRef}
            onKeyDown={(event) => handleEnter(event, onLookup)}
            onChange={(event) => {
              form.setFieldValue(CONSTANTS.form.emailFieldName, event.target.value);
            }}
          />
        </Form.Item>

        <Button loading={isLoading} onClick={onLookup} type="submit" variant="primary">
          {labels.cta.continue}
        </Button>
      </StyledAuthFormContainer>

      <Disclaimer {...labels.disclaimer} />
    </>
  );
};
